// -------------------------
// React
// -------------------------
import React, { useEffect, useState, useMemo, useRef } from "react";
// -------------------------
// Third-party libraries
// -------------------------
import { Card, Col, Row, Button, Offcanvas } from "react-bootstrap";
import {
  ClockHistory,
  ConeStriped,
  ExclamationDiamond,
  ExclamationOctagon,
  ExclamationTriangle,
  Hash,
  Hourglass,
  Reception4,
  Soundwave,
  UpcScan,
} from "react-bootstrap-icons";

import RealtimeDotsGraph from "../graphs/RealtimeDotsGraph";
import DotsGraph from "../graphs/DotsGraph";

// -------------------------
// Utilities / helpers
// -------------------------
import { COLORS } from "utils/colors";
import { formatFrequency, formatDuration } from "utils/helpers/formatters";

// -------------------------
// Constants
// -------------------------
import { StationStatus, AlertState } from "../../utils/constants";

const runtimeConfig = window.RUNTIME_CONFIG || {};

const severityConfig = {
  INFO: {
    color: "text-success",
    label: "Informational",
    Icon: ExclamationTriangle,
  },
  WARNING: {
    color: "text-warning",
    label: "Warning",
    Icon: ExclamationDiamond,
  },
  CRITICAL: {
    color: "text-danger",
    label: "Critical",
    Icon: ExclamationOctagon,
  },
};

const titleStyles = {
  INFO: "text-success",
  WARNING: "blink text-warning",
  CRITICAL: "blink text-danger",
};

// -------------------------
// Component
// -------------------------
/**
 * InterferenceAlertCard
 *
 * Displays detailed information about a single interference alert.
 *
 * Props:
 * @param {Object} alertDetails - The alert information.
 * @param {string} alertDetails.id - Unique identifier for the alert.
 * @param {string} alertDetails.state - Current state of the alert (FIRING/STOPPED).
 * @param {string} alertDetails.type - Type of the alert.
 * @param {string} alertDetails.eventType - Event type description.
 * @param {string} alertDetails.startTime - ISO string of when the alert started.
 * @param {string} alertDetails.severity - Severity level (INFO/WARNING/CRITICAL).
 * @param {number} alertDetails.duration - Duration in seconds if stopped.
 * @param {number} alertDetails.centralFreq - Central frequency in Hz.
 * @param {number} alertDetails.bandwidth - Bandwidth in Hz.
 */
export const InterferenceAlertCard = React.memo(
  ({ alertDetails, stationState, isSummary, onViewMore }) => {
    // -------------------------
    // Hooks
    // -------------------------
    // Tracks the current duration of the alert (in seconds)
    const [duration, setDuration] = useState(0);

    // Ref to store the interval timer
    const durationTimer = useRef(null);

    const [isExpired, setExpired] = useState(false);

    const getInitialDuration = () => {
      if (!alertDetails.startTime) return 0;
      let diffMs;

      if (!alertDetails.duration)
        diffMs = Date.now() - new Date(alertDetails.startTime).getTime();
      else diffMs = alertDetails.duration * 1000;
      return Math.max(0, Math.floor(diffMs / 1000));
    };


    const startTimers = () =>{

      if (durationTimer.current) return;

      setDuration(getInitialDuration());
        // Start timer
        durationTimer.current = setInterval(() => {
          setDuration((prev) => prev + 1);
        }, 1000);
    }

    const clearAllTimers = () => {
      
      setDuration(getInitialDuration());

      if (durationTimer.current) {
        clearInterval(durationTimer.current);
        durationTimer.current = null;
      }
    };

    useEffect(() => {
      // Helper to compute initial duration (in seconds)


      if (alertDetails.state === AlertState.FIRING && !isExpired) {
        // Start timer
        startTimers();

      } else {
        clearAllTimers();
      }
      // Cleanup on unmount or state change
      return () => {
        clearAllTimers();
      };
    }, [alertDetails.state, alertDetails.startTime]);

    useEffect(() => {
    
      if (stationState===StationStatus.state.DISCONNECTED )
      {
        setExpired(true);
        clearAllTimers();
      }
      else{
        startTimers();
        setExpired(false);
      }
    }, [stationState]);

    // Alert title JSX based on severity and state
    const title = useMemo(() => {
      const className =
        alertDetails.state === AlertState.FIRING && !isExpired
          ? titleStyles[alertDetails.severity]
          : "text-secondary";
      return (
        <Card.Title className={className}>
          {alertDetails.eventType} event is{" "}
          {alertDetails.state === AlertState.FIRING && !isExpired
            ? "active"
            : "stopped"}
        </Card.Title>
      );
    }, [
      alertDetails.severity,
      alertDetails.state,
      alertDetails.eventType,
      isExpired,
    ]);

    // Severity display JSX (icon + label)
    const severity = useMemo(() => {
      const { color, label, Icon } =
        severityConfig[alertDetails.severity] ?? severityConfig.INFO;

      return (
        <Row
          className="align-items-center justify-content-center text-center"
          style={{ opacity: 0.8 }}
        >
          <Icon className={color} size={60} />
          <span className={color}>{label}</span>
        </Row>
      );
    }, [alertDetails.severity]);

    const handleClose = () => {
      setShowDetails(false);
    };

    const renderDetails = () => {
      return <div style={{ height: 100 }}></div>;
    };

    // -------------------------
    // JSX
    // -------------------------
    return (
      <>
        <Card
          className="m-2 p-0 border-danger"
          style={{ background: "transparent" }}
        >
          <Card.Body
            style={{ background: COLORS.ligthGrey }}
            className="rounded border-danger"
          >
            <Row className="p-0 m-0">
              <Col className="mt-0 pt-0">
                {title}
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <Hash className="me-1 text-primary"></Hash>
                  <span>Id: {alertDetails.id}</span>
                </Card.Text>
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ConeStriped className="me-1 text-primary"></ConeStriped>
                  <span>
                    State:{" "}
                    {!isExpired ? alertDetails.state : AlertState.STOPPED}
                  </span>
                </Card.Text>
                {alertDetails.type && (
                  <Card.Text className="small p-0 m-0 d-flex align-items-center">
                    <Reception4 className="me-1 text-primary"></Reception4>
                    <span>Type: {alertDetails.type}</span>
                  </Card.Text>
                )}

                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ClockHistory className="me-1 text-primary"></ClockHistory>
                  <span>
                    Start time: {new Date(alertDetails.startTime).toISOString()}
                  </span>
                </Card.Text>
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <Hourglass className="me-1 text-primary"></Hourglass>
                  <span>
                    Duration:{" "}
                    {alertDetails.state === AlertState.FIRING
                      ? formatDuration(duration)
                      : formatDuration(alertDetails.duration)}
                  </span>
                </Card.Text>

                {alertDetails.centralFreq && !isSummary && (
                  <Card.Text className="small p-0 m-0">
                    <Soundwave className="me-1 text-primary"></Soundwave>
                    <span>
                      Central freq.: {formatFrequency(alertDetails.centralFreq)}
                    </span>
                  </Card.Text>
                )}
                {alertDetails.bandwidth && !isSummary && (
                  <Card.Text className="small p-0 m-0 d-flex align-items-center">
                    <UpcScan className="me-1 text-primary"></UpcScan>
                    <span>
                      Bandwidth: {formatFrequency(alertDetails.bandwidth)}
                    </span>
                  </Card.Text>
                )}
                {!isSummary && runtimeConfig.ROUTES.ENABLE_INTERFERENCE_GNSS && (
                  <Card.Text className="small p-0 m-0">
                    <Button
                      className="ps-0 pt-1 pb-0"
                      variant="link"
                      size="sm"
                      onClick={() => {
                        if (onViewMore) {
                          onViewMore(alertDetails);
                        }
                      }}
                    >
                      <b>View more details...</b>
                    </Button>
                  </Card.Text>
                )}
              </Col>
              <Col
                xl={2}
                className="d-flex justify-content-end align-items-center text-center mt-0 pt-0"
              >
                {severity}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </>
    );
  },
);
